import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';

import { checkSearchConditions } from '@travel/traveler-core/components/DataLayer/utils';
import NavigationTab from '@travel/traveler-core/components/NavigationTab';
import { MOBILE_SCREEN, useHasMounted, useTranslation } from '@travel/traveler-core/hooks';
import useDeviceType from '@travel/traveler-core/hooks/useDeviceType';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import { numOfGuestParser } from '@travel/traveler-core/utils';
import { LoadingMask } from '@travel/ui';
import { cx, lazyWithSuspense } from '@travel/utils';

import Breadcrumb from 'components/Breadcrumb';
import DataLayer from 'components/DataLayer';
import type { Section } from 'components/ScrollingNavigation';
import ScrollingNavigation from 'components/ScrollingNavigation';
import type { SearchFormRef } from 'components/SearchForm/types';

import { getIsFetching as getIsBSFetching } from 'store/bookingStep/selectors';
import {
  getHasHighlights,
  getIsDirectContractType,
  getIsFetching as getIsDetailFetching,
  getItem as getProviderItem,
  getProviderFeatures,
} from 'store/providerInformation/selectors';
import { getPlaceName } from 'store/providerList/selectors';
import {
  getFilters as getPlanFilters,
  getIsFetching as getIsPlanListFetching,
} from 'store/providerPlanList/selectors';
import {
  getAcquiredCoupon,
  getFilters as getRoomFilters,
  getGspReqId,
  getIsFetching as getIsRoomListFetching,
  getItems as getPlans,
  getPlansTotal as getRoomsTotal,
  getSalesPromotions,
  getSelectedObjectivesFeatureIdMapping,
  getShouldRenderSimilarProviders,
} from 'store/providerRatePlan/selectors';
import { getReviewListScoreInformation } from 'store/providerReview/selectors';
import { getSEOData } from 'store/seo/selectors';
import { isClient } from 'store/utils';

import { autoFilledCheckInDate, autoFilledCheckOutDate } from 'constants/date';
import { EMPTY_ARRAY } from 'constants/defaultValue';
import { SORT_OPTIONS_DEFAULT_TRANSLATION } from 'constants/provider';
import { TAB_ID } from 'constants/providerInfoTabIds';
import type { PriceRangeType } from 'hooks/useFilterCount';
import useProviderFeatureFilter from 'hooks/useProviderFeatureFilter';
import type { ProviderInfoListTypes, ProviderInfoQuery } from 'ProviderInformation-Types';

import usePageLoaderOnInitial from '../../hooks/usePageLoaderOnInitial';
import BackToTopButton from './components/BackToTopButton';
import BasicInformation from './components/BasicInformation';
import FindARoomButton from './components/FindARoomButton';
import Overview from './components/Overview';
import ProviderInformationHeader from './components/ProviderInformationHeader';
import RoomAndPlanList from './components/RoomAndPlanList';
import SearchBar from './components/SearchBar';
import { createScondIds, generateProviderInfoJsonLd } from './helpers';
import type { URLParams } from './resolver';

import styles from './providerInformation.module.scss';

const SimilarProviders = lazyWithSuspense(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-SimilarProviders" */
      './components/SimilarProviders'
    ),
  { ssr: false },
);

// const BasicInformation = lazyWithSuspense(
//   () =>
//     import(
//       /* webpackMode: "lazy" */
//       /* webpackFetchPriority: "low" */
//       /* webpackChunkName: "component-BasicInformation" */
//       './components/BasicInformation'
//     ),
//   { ssr: false },
// );

const ProviderReviews = lazyWithSuspense(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-ProviderReviews" */
      './components/ProviderReviews'
    ),
  { ssr: false },
);

const Highlights = lazyWithSuspense(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-Highlights" */
      './components/Highlights'
    ),
  { ssr: false },
);

type Props = {
  query: ProviderInfoQuery;
  params?: URLParams;
  /* This is mostly for testing so that this will be false by default on testing,
    except for in the router where it is true */
  shouldLazyLoad?: boolean;
};

const DATE_FORMAT = 'YYYYMMDD';

function ProviderInformation(props: Props) {
  const {
    query,
    params = { pageIdentifier: '', locationPath: '' },
    shouldLazyLoad = false,
  } = props;
  const queryParams = useMemo(() => {
    const startDate =
      query.startDate && query.endDate
        ? query.startDate
        : autoFilledCheckInDate().format(DATE_FORMAT);
    const endDate =
      query.endDate && query.startDate
        ? query.endDate
        : autoFilledCheckOutDate().format(DATE_FORMAT);

    return {
      ...query,
      startDate,
      endDate,
    };
  }, [query]);

  const {
    activeTabId: queryActiveTabId,
    freeCancellation: queryFreeCancellation,
    maxPrice: queryMaxPrice,
    minPrice: queryMinPrice,
    sortKey: querySortKey,
    features: queryFeatures,
    listType: queryListType,
    startDate,
    endDate,
    adults,
    childrenAges,
    noOfUnits,
  } = queryParams;

  const reviewScoreInfo = useSelector(getReviewListScoreInformation);
  const hasHighlights = useSelector(getHasHighlights);
  const providerItem = useSelector(getProviderItem);
  const areaInfo = providerItem.areaInfo;
  const areaName = areaInfo?.city || areaInfo?.district || areaInfo?.region || areaInfo?.country;
  const isDirectContractType = useSelector(getIsDirectContractType);
  const itemPlans = useSelector(getPlans);
  const gspReqId = useSelector(getGspReqId);
  const seoData = useSelector(getSEOData);

  const { isIndexedByDefault } = seoData;

  const acquiredCoupon = useSelector(getAcquiredCoupon);
  const placeName = useSelector(getPlaceName);

  const [activeTabId, setActiveTabId] = useState<string>(queryActiveTabId || TAB_ID.OVERVIEW);
  const [visitedTab, setVisitedTab] = useState<Record<string, string>>({
    ...(queryActiveTabId && { [queryActiveTabId]: queryActiveTabId }),
  });
  const [shouldRenderAll, setRenderAll] = useState(!shouldLazyLoad || false);
  const [isOpenDatePicker, setIsOpenDatePicker] = useState(false);

  const objectiveFeatureMappingIds = useSelector(getSelectedObjectivesFeatureIdMapping);
  const searchFreeCancellation = queryFreeCancellation === 'true';
  const searchedMaxPrice = queryMaxPrice ? Number(queryMaxPrice) : null;
  const searchedMinPrice = queryMinPrice ? Number(queryMinPrice) : null;

  const [maxPrice, setMaxPrice] = useState<PriceRangeType>(searchedMaxPrice);
  const [minPrice, setMinPrice] = useState<PriceRangeType>(searchedMinPrice);
  const [sortKey, setSortKey] = useState(querySortKey || 'price_low_to_high');
  const [freeCancellation, setFreeCancellation] = useState(searchFreeCancellation);
  const { features, setFeatures, searchFeatures } = useProviderFeatureFilter(
    objectiveFeatureMappingIds,
    queryFeatures,
  );

  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;
  const salesPromotions = useSelector(getSalesPromotions);
  const hasMounted = useHasMounted();

  const itemRatePlanIds = itemPlans.length
    ? itemPlans
        .flatMap((item) => item.plans)
        .map((plan) => plan.itemRatePlanId)
        .join(',')
    : '';

  const itemRatePlanPrices = itemPlans.length
    ? itemPlans
        .flatMap((item) => item.plans)
        .map((plan) => plan.price.total)
        .join(',')
    : '';

  const pageName =
    startDate || endDate
      ? 'hotel:rooms_and_plans_dated_list'
      : 'hotel:rooms_and_plans_undated_list';
  const isDated = startDate || endDate;
  const onPage = isDated ? 'providerInfoDated' : 'providerInfoUndated';

  // page loader
  const isDetailFetching = useSelector(getIsDetailFetching);
  const isRoomListFetching = useSelector(getIsRoomListFetching);
  const isPlanListFetching = useSelector(getIsPlanListFetching);
  const isBookingStepFetching = useSelector(getIsBSFetching);
  const providerFeatures = useSelector(getProviderFeatures);

  const roomFilters = useSelector(getRoomFilters);
  const planFilters = useSelector(getPlanFilters);

  const itemTotal = useSelector(getRoomsTotal);

  const [activeListType, setActiveListType] = useState<ProviderInfoListTypes>(
    queryListType ? queryListType : PROVIDER_LIST_TYPE.ROOM_LIST,
  );

  const isRoomListType = activeListType === PROVIDER_LIST_TYPE.ROOM_LIST;
  const filters = isRoomListType ? roomFilters : planFilters;

  const hasSimilarProviders = useSelector(getShouldRenderSimilarProviders);

  const searchBarRef = useRef<SearchFormRef>(null);

  const overviewRef = useRef<HTMLDivElement | null>(null);
  const roomsAndPlansRef = useRef<HTMLDivElement | null>(null);
  const providerReviewsRef = useRef<HTMLDivElement | null>(null);
  const infoFacilitiesRef = useRef<HTMLDivElement | null>(null);
  const highlightsRef = useRef<HTMLDivElement | null>(null);

  const onSubmit = () => {
    searchBarRef.current?.onSubmit();
  };

  useEffect(() => {
    if (!shouldRenderAll) {
      setTimeout(() => {
        setRenderAll(true);
      }, 500);
    }
  }, [shouldRenderAll]);

  usePageLoaderOnInitial(
    'providerInfo',
    isDetailFetching || isRoomListFetching || isPlanListFetching,
  );

  // Match the ids of the features in query to their values
  const scondIds = useMemo(
    () => createScondIds(objectiveFeatureMappingIds, queryFeatures),
    [objectiveFeatureMappingIds, queryFeatures],
  );

  const onClickSeeMoreReview = useCallback(() => {
    if (isSP) {
      setVisitedTab((curr) => ({
        ...curr,
        [TAB_ID.REVIEWS]: TAB_ID.REVIEWS,
      }));
      setActiveTabId(TAB_ID.REVIEWS);
    } else {
      providerReviewsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isSP]);

  const scrollToRoomAndPlan = useCallback(() => {
    if (isClient) {
      window.scrollTo({
        top: (roomsAndPlansRef.current?.offsetTop ?? 0) - 129, //search bar + navigation bar
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    if (isClient) {
      if (window.history.state?.state?.isUpdate) {
        setTimeout(() => {
          scrollToRoomAndPlan();
        }, 500);
        window.history.replaceState({}, '');
      }
    }
  }, [scrollToRoomAndPlan]);

  const onClickFindARoomButton = useCallback(() => {
    if (isSP) {
      setVisitedTab((curr) => ({
        ...curr,
        [TAB_ID.ROOMS_AND_PLANS]: TAB_ID.ROOMS_AND_PLANS,
      }));
      setActiveTabId(TAB_ID.ROOMS_AND_PLANS);
    } else {
      scrollToRoomAndPlan();
    }
  }, [isSP, scrollToRoomAndPlan]);

  // Components
  const basicInfo = useMemo(
    () => <BasicInformation pageName={pageName} className={styles.contentBox} params={params} />,
    [pageName, params],
  );

  const overview = useMemo(
    () => (
      <Overview
        className={cx(styles.contentBox, !isSP && styles.detailSummary)}
        pageIdentifier={params.pageIdentifier}
        onPage={onPage}
        pageName={pageName}
        onClickSeeMoreReview={onClickSeeMoreReview}
      />
    ),
    [isSP, onClickSeeMoreReview, onPage, pageName, params.pageIdentifier],
  );

  const roomsAndPlans = useMemo(
    () => (
      <RoomAndPlanList
        query={queryParams}
        params={params}
        providerInfoCanonicalUrl={providerItem.pathId}
        shouldLazyLoad={shouldLazyLoad}
        setIsOpenDatePicker={setIsOpenDatePicker}
        activeListType={activeListType}
        setActiveListType={setActiveListType}
        filterValProps={{ maxPrice, minPrice, sortKey, freeCancellation, features, searchFeatures }}
        filtersProps={{
          setMaxPrice,
          setMinPrice,
          setSortKey,
          setFreeCancellation,
          setFeatures,
          onSubmit,
        }}
      />
    ),
    [
      activeListType,
      features,
      freeCancellation,
      maxPrice,
      minPrice,
      params,
      providerItem.pathId,
      queryParams,
      searchFeatures,
      setFeatures,
      shouldLazyLoad,
      sortKey,
    ],
  );

  const similarProviders = useMemo(
    () =>
      hasMounted && (
        <SimilarProviders
          query={queryParams}
          className={
            isSP && activeTabId !== TAB_ID.ROOMS_AND_PLANS ? styles.hidden : styles.fullWidthContent
          }
          pageIdentifier={params.pageIdentifier}
        />
      ),
    [activeTabId, isSP, params.pageIdentifier, queryParams, hasMounted],
  );

  const reviews = useMemo(
    () =>
      hasMounted && (
        <ProviderReviews
          onPage={onPage}
          pageIdentifier={params.pageIdentifier}
          className={styles.contentBox}
        />
      ),
    [onPage, params.pageIdentifier, hasMounted],
  );

  const highlights = useMemo(
    () =>
      hasMounted && hasHighlights && isDirectContractType ? (
        <Highlights
          isDisplayedOnTab={isSP ? activeTabId === TAB_ID.HIGHLIGHTS : null}
          className={styles.contentBox}
        />
      ) : null,
    [isSP, hasHighlights, activeTabId, hasMounted],
  );

  // Labels
  const overviewLabel = useTranslation({ id: 'Provider_Info.Navigation.Overview' });
  const roomsAndPlansLabel = useTranslation({ id: 'Provider_Info.Navigation.Rooms_and_Plans' });
  const reviewsLabel = useTranslation({ id: 'Traveler_Common.Reviews' });
  const basicInfoLabel = useTranslation({ id: 'Provider_Info.Menu.Basic_Info' });
  const highlightsLabel = useTranslation({ id: 'Provider_Info.Menu.Customized' });
  const facilitiesLabel = useTranslation({ id: 'Provider_Info.Provider_Details.Facilities' });

  const tabs = useMemo(
    () =>
      isSP
        ? [
            {
              id: TAB_ID.OVERVIEW,
              title: overviewLabel,
              content: overview,
            },
            {
              id: TAB_ID.ROOMS_AND_PLANS,
              title: roomsAndPlansLabel,
              content: (!shouldLazyLoad || visitedTab[TAB_ID.ROOMS_AND_PLANS]) && roomsAndPlans,
            },
            {
              id: TAB_ID.REVIEWS,
              title: reviewsLabel,
              content: (!shouldLazyLoad || visitedTab[TAB_ID.REVIEWS]) && reviews,
            },
            {
              id: TAB_ID.INFO,
              title: basicInfoLabel,
              content: (!shouldLazyLoad || visitedTab[TAB_ID.INFO]) && basicInfo,
            },
            highlights && isDirectContractType
              ? {
                  id: TAB_ID.HIGHLIGHTS,
                  title: highlightsLabel,
                  content: (!shouldLazyLoad || visitedTab[TAB_ID.HIGHLIGHTS]) && highlights,
                }
              : null,
          ].filter((tab) => tab)
        : EMPTY_ARRAY,
    [
      isSP,
      shouldLazyLoad,
      visitedTab,
      overviewLabel,
      overview,
      roomsAndPlansLabel,
      roomsAndPlans,
      reviewsLabel,
      reviews,
      basicInfoLabel,
      basicInfo,
      highlights,
      highlightsLabel,
    ],
  );

  const scrollingSections = useMemo(
    () =>
      !isSP
        ? ([
            {
              id: TAB_ID.OVERVIEW,
              title: overviewLabel,
              target: overviewRef,
            },
            {
              id: TAB_ID.ROOMS_AND_PLANS,
              title: roomsAndPlansLabel,
              target: roomsAndPlansRef,
            },
            {
              id: TAB_ID.REVIEWS,
              title: reviewsLabel,
              target: providerReviewsRef,
            },
            hasHighlights && isDirectContractType
              ? {
                  id: TAB_ID.HIGHLIGHTS,
                  title: highlightsLabel,
                  target: highlightsRef,
                }
              : null,
            {
              id: TAB_ID.INFO,
              title: facilitiesLabel,
              target: infoFacilitiesRef,
            },
          ] as Section[])
        : EMPTY_ARRAY,
    [
      facilitiesLabel,
      hasHighlights,
      highlightsLabel,
      isSP,
      overviewLabel,
      reviewsLabel,
      roomsAndPlansLabel,
    ],
  );

  // tab scroll logics
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const prevActiveTabIdRef = useRef(activeTabId);

  useEffect(() => {
    if (isSP && anchorRef.current && prevActiveTabIdRef.current !== activeTabId) {
      const offsetTop = anchorRef.current?.offsetTop;
      window.scrollTo(0, offsetTop - 67);
      prevActiveTabIdRef.current = activeTabId;
    }
  }, [activeTabId, isSP]);

  const onChangeTabs = useCallback((id: string) => {
    // to re-calculated the text ellipsis when the specific tab is displayed
    setVisitedTab((curr) => ({
      ...curr,
      [id]: id,
    }));
    setActiveTabId(id);
  }, []);

  const onClickTab = useCallback(
    (event: React.ChangeEvent) => {
      onChangeTabs(event.currentTarget.id);
    },
    [onChangeTabs],
  );

  useEffect(() => {
    if (isSP && isClient) {
      const url = new URL(window.location.toString());
      if (url.searchParams.get('activeTabId') !== activeTabId) {
        url.searchParams.set('activeTabId', activeTabId);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, [activeTabId, isSP]);

  const searchConditionsAndOr = useMemo(
    () => checkSearchConditions(filters || [], queryMaxPrice, queryMinPrice),
    [filters, queryMinPrice, queryMaxPrice],
  );

  const sortBy = querySortKey
    ? SORT_OPTIONS_DEFAULT_TRANSLATION[querySortKey]
    : SORT_OPTIONS_DEFAULT_TRANSLATION.price_low_to_high;

  const searchPageNumber =
    reviewScoreInfo.offset && reviewScoreInfo.limit
      ? reviewScoreInfo.offset / reviewScoreInfo.limit + 1
      : 1;

  return (
    <>
      {isIndexedByDefault && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(generateProviderInfoJsonLd(providerItem, reviewScoreInfo))}
          </script>
        </Helmet>
      )}

      <SearchBar
        searchFormRef={searchBarRef}
        className={styles.searchBarWrapper}
        query={queryParams}
        pageIdentifier={params.pageIdentifier}
        providerItem={providerItem}
        filterFormProps={{
          maxPrice,
          minPrice,
          sortKey,
          freeCancellation,
          features,
          searchFeatures,
        }}
        isAutoOpenDatePicker={isOpenDatePicker}
        setIsAutoOpenDatePicker={setIsOpenDatePicker}
        activeTabId={activeTabId}
        activeListType={activeListType}
      />

      <div className={styles.container}>
        {isBookingStepFetching && <LoadingMask type="page" />}
        <BackToTopButton />
        <ProviderInformationHeader
          onPage={onPage}
          pageName={pageName}
          className={cx(styles.contentBox, !isSP && styles.thumbnailList)}
          pageIdentifier={params.pageIdentifier}
        />
      </div>

      {!isSP && (
        <>
          <div className={styles.container}>
            <ScrollingNavigation
              sections={scrollingSections}
              selectedSection={TAB_ID.OVERVIEW}
              otherChildren={<FindARoomButton onClickFindARoomButton={onClickFindARoomButton} />}
              positionTop={72} // Search Bar height
            />
            <div ref={overviewRef}>{overview}</div>
            <div ref={roomsAndPlansRef}>{roomsAndPlans}</div>
          </div>
          {hasSimilarProviders && similarProviders}
          {shouldRenderAll && (
            <div className={styles.container}>
              <div ref={providerReviewsRef}>{reviews}</div>
              <div ref={highlightsRef}>{highlights}</div>
              <div ref={infoFacilitiesRef}>{basicInfo}</div>
            </div>
          )}
        </>
      )}

      {/* mobile: tab menu */}
      {isSP && (
        <>
          <div className={styles.container}>
            <div ref={anchorRef} />
            <NavigationTab
              isAnimatedOnStart={false}
              onClick={onClickTab}
              onChange={onChangeTabs}
              className={styles.tab}
              tabMenuClassName={styles.tabMenu}
              selectedTab={activeTabId}
              tabs={tabs}
              hasTouchEvents={false} // removed temporarily for TIDP-33847
            />
          </div>
          {hasSimilarProviders && similarProviders}
        </>
      )}

      <div className={styles.container}>
        <Breadcrumb className={styles.breadcrumb} placeLayers={providerItem.breadcrumb} />
        {!isDetailFetching && !isRoomListFetching && !isPlanListFetching && (
          <DataLayer
            abTestingPattern="NA"
            abTestingVariants="{'NA': 'NA'}"
            areaId={areaName}
            areaName={areaName}
            checkIn={startDate}
            checkOut={endDate}
            childrenAges={childrenAges}
            couponSalesPromotion={acquiredCoupon?.salesPromotionId}
            getCouponItem={acquiredCoupon?.ratePlanId ? 1 : undefined}
            hotelRegion={areaInfo?.region || areaInfo?.country}
            isBookable={itemPlans?.length > 0 ? 'bookable' : 'unbookable'}
            isSearchFilterApplied={searchConditionsAndOr !== 'x' ? '1' : '0'}
            itemIds={itemRatePlanIds}
            shopIdList={providerItem.id}
            itemRatePlanPrice={itemRatePlanPrices}
            noOfRooms={noOfUnits}
            numOfAdults={parseInt(adults || '')}
            numOfChildren={childrenAges?.split(',')?.length}
            numOfGuest={numOfGuestParser(adults, childrenAges)}
            pageName={pageName}
            pageType="shop_item"
            providerCategory={providerFeatures.categoryFeatures?.join(',')}
            propertyCity={areaInfo?.city}
            propertyCountry={areaInfo?.country}
            propertyDistrict={areaInfo?.district}
            providerFacilities={providerFeatures.facilitiesFeatures?.join(',')}
            providerGenre={providerFeatures.categoryFeatures}
            salesPromotions={salesPromotions}
            searchCondition={scondIds}
            searchConditionsAndOr={searchConditionsAndOr}
            searchConditionId={scondIds}
            searchFilters={scondIds}
            searchHits={itemTotal}
            searchPageNumber={searchPageNumber}
            searchQuery={placeName}
            searchSortType={sortBy}
            siteSection="hotel"
            totalSearchResults={itemTotal}
            gspReqId={gspReqId}
            updateData={acquiredCoupon?.ratePlanId + '-' + searchPageNumber}
          />
        )}
        {isSP && (
          <FindARoomButton
            activeTabId={activeTabId}
            onClickFindARoomButton={onClickFindARoomButton}
          />
        )}
      </div>
    </>
  );
}

export default ProviderInformation;
